import React from 'react';

/**
 * HowItWorks — reusable 3-step "how it works" section.
 *
 * One component, five variants (general homepage + 4 audiences). Designed to
 * be a surgical insert: it inherits the site's typography + color palette and
 * does not introduce new fonts, gradients, or layout primitives.
 *
 * Variants:
 *   "general"  → homepage (cold mixed traffic)
 *   "athlete"  → /athletes
 *   "parent"   → /parents
 *   "coach"    → /coaches
 *   "school"   → /schools
 */

const TEAL = '#004C54';
const CYAN = '#4FBCC7';

const VARIANTS = {
  general: {
    heading: 'How it works',
    eyebrow: 'The Apex Six method · 30 days to install',
    steps: [
      {
        title: 'Diagnose',
        body: 'Start with a quick assessment, quiz, or discovery call so we can identify the biggest mental performance gaps showing up in training and competition.',
      },
      {
        title: 'Install',
        body: 'Over the next 30 days, we build your Apex plan and install six core tools into routines, practice habits, and game-day preparation.',
      },
      {
        title: 'Apply & refine',
        body: 'Athletes use the tools under pressure in real competition while we review, adjust, and determine the best next step for continued in-season or off-season support.',
      },
    ],
  },
  athlete: {
    heading: 'How it works for athletes',
    eyebrow: 'Built for serious middle school, high school, and college athletes',
    steps: [
      {
        title: 'Identify the gap',
        body: 'We start by identifying where your mindset breaks down most — confidence, pressure, bounce-back, focus, communication, or preparation.',
      },
      {
        title: 'Build your tools',
        body: 'Over 30 days, we install practical mental performance tools you can use before, during, and after training and games.',
      },
      {
        title: 'Compete with them',
        body: 'You take those tools into real competition, learn what works under pressure, and continue refining them through ongoing support if needed.',
      },
    ],
  },
  parent: {
    heading: 'How it works for parents',
    eyebrow: 'A clear path to support your athlete without piling on',
    steps: [
      {
        title: 'Understand what your athlete needs',
        body: 'We begin with an assessment or conversation to understand where your athlete is struggling mentally and what kind of support makes sense.',
      },
      {
        title: 'Create a simple plan',
        body: 'Over the next 30 days, your athlete learns core performance tools while you gain a clearer understanding of how to support confidence, recovery, and consistency at home.',
      },
      {
        title: 'Support long-term growth',
        body: 'As the tools are used in real competition, we refine the plan and decide whether continued coaching or seasonal support is the right next step.',
      },
    ],
  },
  coach: {
    heading: 'How it works for coaches',
    eyebrow: 'Install shared mental performance language across your roster',
    steps: [
      {
        title: 'Assess the mental performance needs of your team',
        body: 'We identify where your athletes or staff need the most support — confidence, communication, composure, accountability, leadership, or response to pressure.',
      },
      {
        title: 'Install shared language and tools',
        body: 'Over 30 days, we introduce practical mental performance systems your athletes and staff can actually use in training, meetings, and competition.',
      },
      {
        title: 'Reinforce the culture',
        body: 'Your team applies the tools in live environments while we refine implementation and determine the best structure for ongoing team, staff, or seasonal support.',
      },
    ],
  },
  school: {
    heading: 'How it works for schools and clubs',
    eyebrow: 'Program-level mental performance, leadership, and culture support',
    steps: [
      {
        title: 'Evaluate the program needs',
        body: 'We start by identifying the biggest mental performance, leadership, and culture needs across your athletes, teams, coaches, or parent community.',
      },
      {
        title: 'Build a tailored support plan',
        body: 'We build a rollout plan that fits your calendar — typically starting with a launch session, then a sequenced program across the year, with your coaches certified to run it as the year goes on.',
      },
      {
        title: 'Strengthen performance and culture over time',
        body: 'We help your program apply the work in real environments and can continue supporting athletes, teams, and stakeholders through seasonal or ongoing partnerships.',
      },
    ],
  },
};

const HowItWorks = ({
  variant = 'general',
  // Tone of the surrounding section. "dark" surfaces inherit white text /
  // teal accents; "light" surfaces use the inverse (default).
  tone = 'light',
  className = '',
}) => {
  const v = VARIANTS[variant] || VARIANTS.general;
  const isDark = tone === 'dark';

  const eyebrowColor = isDark ? CYAN : TEAL;
  const headingColor = isDark ? '#ffffff' : '#0a0d0e';
  const bodyColor = isDark ? 'rgba(255,255,255,0.78)' : '#3a3a3a';
  const cardBg = isDark ? 'rgba(255,255,255,0.04)' : '#fbf7f1';
  const cardBorder = isDark ? 'rgba(79,188,199,0.18)' : 'rgba(0,76,84,0.10)';

  return (
    <section
      className={`py-16 sm:py-20 ${className}`}
      data-testid={`how-it-works-${variant}`}
      aria-label={v.heading}
    >
      <div className="max-w-[1120px] mx-auto px-5 sm:px-8">
        <div className="text-center mb-10 sm:mb-12">
          <div
            className="font-mono-accent uppercase tracking-[0.24em] text-[10.5px] sm:text-[11px] font-bold mb-3"
            style={{ color: eyebrowColor }}
            data-testid={`how-it-works-${variant}-eyebrow`}
          >
            {v.eyebrow}
          </div>
          <h2
            className="font-heading text-[1.9rem] sm:text-[2.4rem] font-bold tracking-tight leading-tight"
            style={{ color: headingColor }}
            data-testid={`how-it-works-${variant}-heading`}
          >
            {v.heading}
          </h2>
        </div>

        <ol className="grid gap-5 md:gap-6 md:grid-cols-3">
          {v.steps.map((step, i) => (
            <li
              key={step.title}
              className="rounded-2xl p-6 sm:p-7 border flex flex-col"
              style={{ background: cardBg, borderColor: cardBorder }}
              data-testid={`how-it-works-${variant}-step-${i + 1}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <span
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full text-[13px] font-bold"
                  style={{ background: TEAL, color: '#fff' }}
                  aria-hidden="true"
                >
                  {i + 1}
                </span>
                <span
                  className="font-mono-accent uppercase tracking-[0.18em] text-[10px] font-bold"
                  style={{ color: eyebrowColor }}
                >
                  Step {i + 1}
                </span>
              </div>
              <h3
                className="font-heading text-[18px] sm:text-[19px] font-bold leading-snug mb-2"
                style={{ color: headingColor }}
              >
                {step.title}
              </h3>
              <p
                className="text-[14px] sm:text-[14.5px] leading-relaxed"
                style={{ color: bodyColor }}
              >
                {step.body}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default HowItWorks;
export { HowItWorks };
